'use client';

import React, { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import Link from 'next/link';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ReCaptchaV3Provider } from './components/ReCaptchaV3Provider';
import { ValidationRules } from './components/FormValidation';
import { useUtmTracking } from '@/hooks/useUtmTracking';
import { baseFormOptions, getStatesForCountry, shouldUseTextInput, getStateFieldLabel } from './steps/baseFormOptions';

// Form data interface
interface ContactFormData {
  firstName: string;
  lastName: string;
  businessName: string;
  phone: string;
  email: string;
  country: string;
  state: string;
}

interface CPAEventFormProps {
  onSubmit?: (data: ContactFormData) => void | Promise<void>;
  className?: string;
  // Builder.io configuration props
  title?: string;
  description?: string;
  submitButtonText?: string;
  formId?: string;
}

// Original component without reCAPTCHA (for backward compatibility)
export const CPAEventForm: React.FC<CPAEventFormProps> = (props) => {
  return <CPAEventFormWithCaptcha {...props} />;
};


// Inner component that uses reCAPTCHA hooks
const CPAEventFormInner: React.FC<CPAEventFormProps> = ({
  onSubmit,
  title,
  description,
  submitButtonText = "Submit",
  formId = "cpa-event-form"
}) => {
  const form = useForm<ContactFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      businessName: '',
      phone: '',
      email: '',
      country: 'Australia',
      state: '',
    },
    mode: 'onChange', // Validate on change
  });

  const { isSubmitting, isValid } = form.formState;
  const { control } = form;
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [, setRecaptchaToken] = useState<string | null>(null);
  
  const { executeRecaptcha } = useGoogleReCaptcha();
  const utmParams = useUtmTracking();

  // Watch country field for dynamic state selection
  const selectedCountry = useWatch({ 
    control, 
    name: "country" as never 
  }) as unknown as string | undefined;

  // Dynamic state logic based on selected country
  const dynamicStates = selectedCountry ? getStatesForCountry(selectedCountry) : [];
  const useTextInput = selectedCountry ? shouldUseTextInput(selectedCountry) : false;
  const stateFieldLabel = selectedCountry ? getStateFieldLabel(selectedCountry) : "State";

  // Clear state only when country actually changes to a different value
  const prevCountryRef = React.useRef<string | undefined>(undefined);
  
  React.useEffect(() => {
    if (prevCountryRef.current !== undefined && prevCountryRef.current !== selectedCountry) {
      form.setValue('state', '');
    }
    prevCountryRef.current = selectedCountry;
  }, [selectedCountry, form]);

  // Execute reCAPTCHA v3 - get token only (10s timeout, 1 retry on failure; verification happens server-side)
  const executeRecaptchaV3 = async (): Promise<{ token: string } | null> => {
    if (!executeRecaptcha) {
      console.warn('reCAPTCHA v3 not ready - executeRecaptcha is undefined');
      return null;
    }

    const RECAPTCHA_TIMEOUT_MS = 10000;

    const attempt = async (): Promise<{ token: string }> => {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('reCAPTCHA timeout after 10 seconds')), RECAPTCHA_TIMEOUT_MS)
      );
      const token = await Promise.race([executeRecaptcha('contact_form_submit'), timeoutPromise]) as string;
      if (!token) throw new Error('No token');
      setRecaptchaToken(token);
      return { token };
    };

    for (let i = 0; i < 2; i++) {
      try {
        return await attempt();
      } catch (error) {
        if (i === 1) {
          console.error('reCAPTCHA v3 execution failed:', error);
          setSubmitError('Security verification failed. Please try again.');
          return null;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (data: ContactFormData) => {
    try {
      // Clear any previous errors
      setSubmitError(null);
      
      // Execute reCAPTCHA v3 first
      const captchaResult = await executeRecaptchaV3();
      if (!captchaResult) {
        setSubmitError('Security verification failed. Please try again.');
        return;
      }

      // Proceed with form submission
      if (onSubmit) {
        await onSubmit(data);
      } else {
        // Map form data to Dynamics field format

        const topic = data.firstName + ' ' + data.lastName + ' - CPA Public Practice Retreat 2026';

        const dynamicsPayload = {
          ot_topic: topic,
          ot_firstname: data.firstName,
          ot_lastname: data.lastName,
          ot_businessname: data.businessName,
          ot_phone: data.phone,
          ot_workemail: data.email,
          ot_country: data.country,
          ot_state: data.state,
          ot_interestedservice: 'PPP',
          pb_partner: 'CPA Australia Ltd',
          ot_industry: 'Financial',
          pb_inquirytopic: 'Sales Related',
          ...utmParams,
          // Include reCAPTCHA token (will be verified server-side)
          recaptchaToken: captchaResult.token,
        };
        
        // Submit to Dynamics via API route
        const response = await fetch('/api/dynamics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dynamicsPayload),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || result.error || 'Failed to submit form');
        }

        console.info('Form submitted successfully:', result);
      }
      
      // Show thank you content after successful submission
      setShowThankYou(true);
      
      // Scroll to top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit form. Please try again.');
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl shadow-lg px-6 py-10" id={formId}>
      {showThankYou ? (
        // Thank You Content
        <div className="text-center">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <span className="material-symbols-outlined font-variation-filled text-green-600 size-12" style={{ fontSize: '48px' }}>
              check_circle
            </span>
          </div>

          {/* Main Heading */}
          <h3 className=" mb-4">
            Thank you
          </h3>

          {/* Confirmation Message */}
          <p className="paragraph-lg mb-12">
            We&apos;ve received your message and our team will be in touch soon.
          </p>

          {/* Follow-up Suggestion */}
          <p className="mb-4 paragraph-base">
            While you wait, why not check out our latest guides and insights?
          </p>

          {/* Call-to-Action Button */}
 
            <Button 
              asChild
              size="lg"
              variant="outline" 
              className="w-full border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white transition-colors"
            >
              <Link href="https://www.linkedin.com/company/octet" target="_blank" rel="noopener noreferrer">
                Follow Octet on LinkedIn
              </Link>
            </Button>
 
        </div>
      ) : (
        // Form Content
        <>
          {/* Optional title and description */}
          {title && (
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">{title}</h2>
              {description && (
                <p className="text-muted-foreground">{description}</p>
              )}
            </div>
          )}

          {/* Error Message */}
          {submitError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex gap-2">
                <span className="material-symbols-outlined text-red-500">error</span>
                <div>
                  <p className="text-red-500 font-bold">Submission Error</p>
                  <p className="text-red-500">Having trouble? You can <Link href="/contact-us">contact support</Link> or try refreshing the page.</p>
                </div>
              </div>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
              {/* Name Fields - Two columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <FormField
                  control={form.control}
                  name="firstName"
                  rules={ValidationRules.required('First name')}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel >First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="" {...field} maxLength={500} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  rules={ValidationRules.required('Last name')}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel >Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="" {...field} maxLength={500} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Business Name - Full width */}
              <FormField
                control={form.control}
                name="businessName"
                rules={ValidationRules.businessName()}
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel >Business Name</FormLabel>
                    <FormControl>
                      <Input placeholder="" {...field} maxLength={500} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Contact Fields - Two columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <FormField
                  control={form.control}
                  name="phone"
                  rules={ValidationRules.phone()}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel >Phone</FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="" {...field} maxLength={100} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  rules={ValidationRules.email()}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel >Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="" {...field} maxLength={500} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {/* Country Field */}
                <FormField
                  control={form.control}
                  name="country"
                  rules={ValidationRules.selection('country')}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel >Country</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {baseFormOptions.allCountries.map((country) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* State Field - Dynamic based on country */}
                <FormField
                  control={form.control}
                  name="state"
                  rules={{
                    required: useTextInput 
                      ? `Please enter a ${stateFieldLabel.toLowerCase()}`
                      : `Please select a ${stateFieldLabel.toLowerCase()}`,
                    validate: (value: unknown) => {
                      // State is always required
                      const stringValue = typeof value === 'string' ? value : String(value || '');
                      if (!value || stringValue.trim() === '') {
                        return useTextInput
                          ? `Please enter a ${stateFieldLabel.toLowerCase()}`
                          : `Please select a ${stateFieldLabel.toLowerCase()}`;
                      }
                      return true; // Valid if value is selected
                    }
                  }}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{stateFieldLabel}</FormLabel>
                      <FormControl>
                        {useTextInput ? (
                          <Input
                            placeholder=""
                            value={(field.value as string) || ""}
                            onChange={field.onChange}
                            maxLength={100}
                          />
                        ) : (
                          <Select
                            value={(field.value as string) || ""}
                            onValueChange={(value) => { field.onChange(value); }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {dynamicStates.map((s: { code: string; name: string }) => (
                                <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                variant="primary"
                size="lg"
                disabled={isSubmitting || !isValid}
                className="w-full mb-2"
              >
                {isSubmitting ? 'Submitting...' : submitButtonText}
              </Button>
              
              {/* Privacy Information */}
              {/* <p className="paragraph-sm text-muted-foreground text-center">
                For more information about how we handle your personal information, please visit our{' '}
                <Link href={privacyPolicyUrl} className="text-primary hover:underline">
                  privacy policy
                </Link>
                . This site and form is protected by reCAPTCHA.
              </p> */}
            </form>
          </Form>
        </>
      )}
    </div>
  );
};

// Wrapper component that provides reCAPTCHA v3 context
export const CPAEventFormWithCaptcha: React.FC<CPAEventFormProps> = (props) => {
  return (
    <ReCaptchaV3Provider>
      <CPAEventFormInner {...props} />
    </ReCaptchaV3Provider>
  );
};
